import type { Mutable, ObjectType } from "models-webgl2";
import { arrayOf } from "utils/helpers/array-of";
import { isWithinRange } from "../is-within-range";
import { Vec2 } from "../vec2";

export class Mat3 {
  public mat: Float32Array;

  private _savedMat = new Float32Array(9);
  private _isSaved = false;

  constructor() {
    this.mat = new Float32Array(9);
  }

  public clone() {
    return new Mat3().set(
      this.mat[0],
      this.mat[1],
      this.mat[2],
      this.mat[3],
      this.mat[4],
      this.mat[5],
      this.mat[6],
      this.mat[7],
      this.mat[8],
    );
  }

  public copy(from: ArrayLike<number>) {
    Mat3.copy(this.mat, from);
    return this;
  }

  public save() {
    this._isSaved = true;
    Mat3.copy(this._savedMat, this.mat);
    return this;
  }

  public restore() {
    if (this._isSaved) {
      this._isSaved = false;
      Mat3.copy(this.mat, this._savedMat);
    }
    return this;
  }

  public set(
    m00: number,
    m01: number,
    m02: number,
    m10: number,
    m11: number,
    m12: number,
    m20: number,
    m21: number,
    m22: number,
  ) {
    this.mat[0] = m00;
    this.mat[1] = m01;
    this.mat[2] = m02;
    this.mat[3] = m10;
    this.mat[4] = m11;
    this.mat[5] = m12;
    this.mat[6] = m20;
    this.mat[7] = m21;
    this.mat[8] = m22;
    return this;
  }

  public setIndex(i: number, value: number) {
    if (!isWithinRange(i, 0, this.mat.length - 1)) return this;
    this.mat[i] = value;
    return this;
  }

  public fill(num: number) {
    this.mat[0] = num;
    this.mat[1] = num;
    this.mat[2] = num;
    this.mat[3] = num;
    this.mat[4] = num;
    this.mat[5] = num;
    this.mat[6] = num;
    this.mat[7] = num;
    this.mat[8] = num;
    return this;
  }

  public translate(x: number, y: number): Mat3;
  public translate(vec: Vec2): Mat3;
  public translate(a: any, b?: any): Mat3 {
    const isVec = a instanceof Vec2;
    const x = isVec ? a.x : a;
    const y = isVec ? a.y : b;

    this.mat[6] = x * this.mat[0] + y * this.mat[3] + this.mat[6];
    this.mat[7] = x * this.mat[1] + y * this.mat[4] + this.mat[7];
    this.mat[8] = x * this.mat[2] + y * this.mat[5] + this.mat[8];

    return this;
  }

  public rotate(rad: number) {
    const a00 = this.mat[0];
    const a01 = this.mat[1];
    const a02 = this.mat[2];
    const a10 = this.mat[3];
    const a11 = this.mat[4];
    const a12 = this.mat[5];

    const s = Math.sin(-rad);
    const c = Math.cos(-rad);

    this.mat[0] = c * a00 + s * a10;
    this.mat[1] = c * a01 + s * a11;
    this.mat[2] = c * a02 + s * a12;

    this.mat[3] = c * a10 - s * a00;
    this.mat[4] = c * a11 - s * a01;
    this.mat[5] = c * a12 - s * a02;

    return this;
  }

  public rotateAround(rad: number, oX: number, oY: number) {
    this.translate(oX, oY);
    this.rotate(rad);
    this.translate(-oX, -oY);
    return this;
  }

  public scale(x: number, y: number): Mat3;
  public scale(vec: Vec2): Mat3;
  public scale(a: any, b?: any): Mat3 {
    const isVec = a instanceof Vec2;
    const x = isVec ? a.x : a;
    const y = isVec ? a.y : b;

    this.mat[0] = this.mat[0] * x;
    this.mat[1] = this.mat[1] * x;
    this.mat[2] = this.mat[2] * x;

    this.mat[3] = this.mat[3] * y;
    this.mat[4] = this.mat[4] * y;
    this.mat[5] = this.mat[5] * y;

    return this;
  }

  public scaleNew(x: number, y: number): Mat3;
  public scaleNew(vec: Vec2): Mat3;
  public scaleNew(a: any, b?: any): Mat3 {
    const isVec = a instanceof Vec2;
    const x = isVec ? a.x : a;
    const y = isVec ? a.y : b;

    return new Mat3().set(
      this.mat[0] * x,
      this.mat[1] * x,
      this.mat[2] * x,
      this.mat[3] * y,
      this.mat[4] * y,
      this.mat[5] * y,
      this.mat[6],
      this.mat[7],
      this.mat[8],
    );
  }

  public flipX() {
    this.mat[0] = -this.mat[0];
    return this;
  }

  public flipY() {
    this.mat[4] = -this.mat[4];
    return this;
  }

  public flipXY() {
    this.mat[0] = -this.mat[0];
    this.mat[4] = -this.mat[4];
    return this;
  }

  public invert() {
    const a00 = this.mat[0];
    const a01 = this.mat[1];
    const a02 = this.mat[2];
    const a10 = this.mat[3];
    const a11 = this.mat[4];
    const a12 = this.mat[5];
    const a20 = this.mat[6];
    const a21 = this.mat[7];
    const a22 = this.mat[8];

    const b01 = a22 * a11 - a12 * a21;
    const b11 = -a22 * a10 + a12 * a20;
    const b21 = a21 * a10 - a11 * a20;

    // Calculate the determinant
    let det = a00 * b01 + a01 * b11 + a02 * b21;

    if (!det) return this;
    det = 1.0 / det;

    this.mat[0] = b01 * det;
    this.mat[1] = (-a22 * a01 + a02 * a21) * det;
    this.mat[2] = (a12 * a01 - a02 * a11) * det;
    this.mat[3] = b11 * det;
    this.mat[4] = (a22 * a00 - a02 * a20) * det;
    this.mat[5] = (-a12 * a00 + a02 * a10) * det;
    this.mat[6] = b21 * det;
    this.mat[7] = (-a21 * a00 + a01 * a20) * det;
    this.mat[8] = (a11 * a00 - a01 * a10) * det;

    return this;
  }

  public multiply(matrix: ArrayLike<number>) {
    const a00 = this.mat[0];
    const a01 = this.mat[1];
    const a02 = this.mat[2];
    const a10 = this.mat[3];
    const a11 = this.mat[4];
    const a12 = this.mat[5];
    const a20 = this.mat[6];
    const a21 = this.mat[7];
    const a22 = this.mat[8];
    const b00 = matrix[0];
    const b01 = matrix[1];
    const b02 = matrix[2];
    const b10 = matrix[3];
    const b11 = matrix[4];
    const b12 = matrix[5];
    const b20 = matrix[6];
    const b21 = matrix[7];
    const b22 = matrix[8];

    this.set(
      b00 * a00 + b01 * a10 + b02 * a20,
      b00 * a01 + b01 * a11 + b02 * a21,
      b00 * a02 + b01 * a12 + b02 * a22,
      b10 * a00 + b11 * a10 + b12 * a20,
      b10 * a01 + b11 * a11 + b12 * a21,
      b10 * a02 + b11 * a12 + b12 * a22,
      b20 * a00 + b21 * a10 + b22 * a20,
      b20 * a01 + b21 * a11 + b22 * a21,
      b20 * a02 + b21 * a12 + b22 * a22,
    );

    return this;
  }

  public multiplyFrom(matrix: ArrayLike<number>) {
    const a00 = matrix[0];
    const a01 = matrix[1];
    const a02 = matrix[2];
    const a10 = matrix[3];
    const a11 = matrix[4];
    const a12 = matrix[5];
    const a20 = matrix[6];
    const a21 = matrix[7];
    const a22 = matrix[8];
    const b00 = this.mat[0];
    const b01 = this.mat[1];
    const b02 = this.mat[2];
    const b10 = this.mat[3];
    const b11 = this.mat[4];
    const b12 = this.mat[5];
    const b20 = this.mat[6];
    const b21 = this.mat[7];
    const b22 = this.mat[8];

    this.set(
      b00 * a00 + b01 * a10 + b02 * a20,
      b00 * a01 + b01 * a11 + b02 * a21,
      b00 * a02 + b01 * a12 + b02 * a22,
      b10 * a00 + b11 * a10 + b12 * a20,
      b10 * a01 + b11 * a11 + b12 * a21,
      b10 * a02 + b11 * a12 + b12 * a22,
      b20 * a00 + b21 * a10 + b22 * a20,
      b20 * a01 + b21 * a11 + b22 * a21,
      b20 * a02 + b21 * a12 + b22 * a22,
    );

    return this;
  }

  public multiplyNew(matrix: ArrayLike<number>) {
    const a00 = this.mat[0];
    const a01 = this.mat[1];
    const a02 = this.mat[2];
    const a10 = this.mat[3];
    const a11 = this.mat[4];
    const a12 = this.mat[5];
    const a20 = this.mat[6];
    const a21 = this.mat[7];
    const a22 = this.mat[8];
    const b00 = matrix[0];
    const b01 = matrix[1];
    const b02 = matrix[2];
    const b10 = matrix[3];
    const b11 = matrix[4];
    const b12 = matrix[5];
    const b20 = matrix[6];
    const b21 = matrix[7];
    const b22 = matrix[8];

    return new Mat3().set(
      b00 * a00 + b01 * a10 + b02 * a20,
      b00 * a01 + b01 * a11 + b02 * a21,
      b00 * a02 + b01 * a12 + b02 * a22,
      b10 * a00 + b11 * a10 + b12 * a20,
      b10 * a01 + b11 * a11 + b12 * a21,
      b10 * a02 + b11 * a12 + b12 * a22,
      b20 * a00 + b21 * a10 + b22 * a20,
      b20 * a01 + b21 * a11 + b22 * a21,
      b20 * a02 + b21 * a12 + b22 * a22,
    );
  }

  public determinant(): number {
    return Mat3.determinant(this.mat);
  }

  public logMatrix(name?: string) {
    Mat3.logMatrix(this.mat, name);
    return this;
  }

  static copy(to: Mutable<ArrayLike<number>>, from: ArrayLike<number>) {
    to[0] = from[0];
    to[1] = from[1];
    to[2] = from[2];
    to[3] = from[3];
    to[4] = from[4];
    to[5] = from[5];
    to[6] = from[6];
    to[7] = from[7];
    to[8] = from[8];
    return to;
  }

  static identity() {
    return new Mat3().set(1, 0, 0, 0, 1, 0, 0, 0, 1);
  }

  static projection(width: number, height: number): Mat3;
  static projection(vec: Vec2): Mat3;
  static projection(a: any, b?: any) {
    const isVec = a instanceof Vec2;
    const width = isVec ? a.width : a;
    const height = isVec ? a.height : b;
    // Note: This matrix flips the Y axis so that 0 is at the top.
    return new Mat3().set(2 / width, 0, 0, 0, -2 / height, 0, -1, 1, 1);
  }

  static translation(tx: number, ty: number) {
    return new Mat3().set(1, 0, 0, 0, 1, 0, tx, ty, 1);
  }

  static rotation(rad: number) {
    const s = Math.sin(rad);
    const c = Math.cos(rad);
    return new Mat3().set(c, -s, 0, s, c, 0, 0, 0, 1);
  }

  static scaling(sx: number, sy: number) {
    return new Mat3().set(sx, 0, 0, 0, sy, 0, 0, 0, 1);
  }

  static determinant(matrix: ArrayLike<number>) {
    const a00 = matrix[0];
    const a01 = matrix[1];
    const a02 = matrix[2];
    const a10 = matrix[3];
    const a11 = matrix[4];
    const a12 = matrix[5];
    const a20 = matrix[6];
    const a21 = matrix[7];
    const a22 = matrix[8];

    return (
      a00 * (a22 * a11 - a12 * a21) + a01 * (-a22 * a10 + a12 * a20) + a02 * (a21 * a10 - a11 * a20)
    );
  }

  static logMatrix(matrix: ArrayLike<number>, name = "") {
    const mat = Array.from(matrix).map(String);

    const row0 = mat.slice(0, 3);
    const row0LengthArr = row0.map((str) => str.length);

    const row1 = mat.slice(3, 6);
    const row1LengthArr = row1.map((str) => str.length);

    const row2 = mat.slice(6, 9);
    const row2LengthArr = row2.map((str) => str.length);

    const lengthMap: ObjectType<number, number> = {
      0: Math.max(row0LengthArr[0], row1LengthArr[0], row2LengthArr[0]),
      1: Math.max(row0LengthArr[1], row1LengthArr[1], row2LengthArr[1]),
      2: Math.max(row0LengthArr[2], row1LengthArr[2], row2LengthArr[2]),
    };

    const getSpaces = (amount: number) => arrayOf(amount, () => " ").join("");

    const mapWithSpaces = (arr: string[]) => {
      return arr.map((str, i) => getSpaces(lengthMap[i] - str.length) + str);
    };

    const toLog = [
      mapWithSpaces(row0).join("  "),
      mapWithSpaces(row1).join("  "),
      mapWithSpaces(row2).join("  "),
    ];

    if (name) toLog.unshift(name + ":");

    // eslint-disable-next-line no-console
    console.log(toLog.join("\n"));
  }
}

export default Mat3;
